import { useState } from "react";
import { Box, useTheme } from "@mui/material";
import {
  NotificationsNoneOutlined as BellIcon,
  CampaignOutlined as PublicationIcon,
  FilterListRounded as SavedScreenIcon,
} from "@mui/icons-material";
import { ScreenerLogo } from "../Screener/ScreenerLogo";
import { isSafeImageSrc } from "../../lib/safeImageSrc";
import { NotificationItem } from "./types";

export const AVATAR_SIZE = 36;
const AVATAR_RADIUS = "8px";

// The screener's logo tile (ScreenerLogo), so a logo, an icon and a picture
// all sit on the same grey square and the column reads as one set.
const tileBackground = (isDark: boolean) => (isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.045)");

// Insight's own alerts and updates keep the grey tile but take a green glyph
// (bar Insight's white mark in dark mode), so they stand apart from the company
// logos down the column.
const BRAND_ICONS = new Set(["screener", "publication", "insight"]);

const brandGlyph = (isDark: boolean) => (isDark ? "#4ade80" : "#15803d");

// Icon keys the server may send as `imageValue`. ANY key this build has never
// heard of renders the bell — a binary in the wild must never show a hole. A
// Map, not an object literal: a key of "constructor" must miss, not resolve.
const ICONS = new Map<string, typeof BellIcon>([
  ["bell", BellIcon],
  ["publication", PublicationIcon],
  ["screener", SavedScreenIcon],
]);

interface IconAvatarProps {
  iconKey: string | null;
  size: number;
}

const IconAvatar = ({ iconKey, size }: IconAvatarProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const Icon = ICONS.get(iconKey ?? "") ?? BellIcon;
  const brand = BRAND_ICONS.has(iconKey ?? "");
  const isInsight = iconKey === "insight";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        width: size,
        height: size,
        borderRadius: AVATAR_RADIUS,
        backgroundColor: tileBackground(isDark),
      }}
    >
      {isInsight && (
        // White on the dark tile; the light tile would swallow white, so green.
        <Box
          component="img"
          src={isDark ? "/insightShortLogoWhite.svg" : "/insightShortLogoPrimary.svg"}
          alt=""
          sx={{ width: size / 2 }}
        />
      )}
      {!isInsight && (
        <Icon sx={{ fontSize: size > AVATAR_SIZE ? 22 : 20, color: brand ? brandGlyph(isDark) : theme.palette.text.secondary }} />
      )}
    </Box>
  );
};

interface NotificationAvatarProps {
  imageKind: NotificationItem["imageKind"];
  imageValue: string | null;
  /** Names the fallback tile's initial when a company has no logo. */
  name?: string;
  /** From Insight itself (a publication): Insight's mark, as its picture is too small to make out here. */
  branded?: boolean;
  size?: number;
}

/** The leading image of a notification row, chosen by `imageKind`. */
const NotificationAvatar = ({
  imageKind,
  imageValue,
  name,
  branded = false,
  size = AVATAR_SIZE,
}: NotificationAvatarProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [failedUrl, setFailedUrl] = useState<string | null>(null);

  const isSymbol = !branded && imageKind === "symbol" && Boolean(imageValue);
  const isImage = !branded && imageKind === "url" && isSafeImageSrc(imageValue) && failedUrl !== imageValue;
  const serverIcon = imageKind === "icon" ? imageValue : null;
  const iconKey = branded ? "insight" : serverIcon;

  return (
    <>
      {isSymbol && <ScreenerLogo symbol={imageValue ?? ""} name={name} size={size} />}
      {isImage && (
        <Box
          component="img"
          src={imageValue ?? undefined}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailedUrl(imageValue)}
          sx={{
            flexShrink: 0,
            width: size,
            height: size,
            borderRadius: AVATAR_RADIUS,
            objectFit: "cover",
            backgroundColor: tileBackground(isDark),
          }}
        />
      )}
      {!isSymbol && !isImage && <IconAvatar iconKey={iconKey} size={size} />}
    </>
  );
};

export default NotificationAvatar;
